import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Lock, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useGroup } from "@/lib/GroupContext";
import { http } from "@/lib/api";

const TONE = {
  unlocked: "border-[5px] border-[#00703c] text-[#005a30]",
  already: "border-[5px] border-[#0033A1] text-[#0033A1]",
  invalid: "form-error error-msg",
};

export function EvidenceLocker() {
  const { state, setState, lockerOpen, setLockerOpen } = useGroup();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await http.post("/case/codes", { code });
      setResult(data);
      setState(data.state);
      setCode("");
    } catch (err) {
      setResult({ result: "invalid", message: "The locker jammed. Try again." });
    } finally {
      setBusy(false);
    }
  };

  const recovered = state ? state.order.filter((s) => state.suspects[s].clue) : [];
  const open = (sid) => {
    setLockerOpen(false);
    navigate(`/suspects/${sid}?tab=clue`);
  };

  return (
    <Dialog open={lockerOpen} onOpenChange={(o) => { setLockerOpen(o); if (!o) setResult(null); }}>
      <DialogContent className="rounded-none border-2 border-[#0b0c0c] max-w-2xl p-10" data-testid="evidence-locker-dialog">
        <DialogHeader>
          <span className="caption">Evidence locker · Operation SPORE</span>
          <DialogTitle className="text-4xl font-black">Submit evidence reference</DialogTitle>
          <DialogDescription className="sr-only">Enter the recovery code from a hidden document.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2">
          <label htmlFor="evidence-code-input" className="form-label">Recovery code</label>
          <span className="form-hint">The 4-digit code printed on the document you found in the asylum.</span>
          <div className="flex gap-4 mt-3">
            <input
              id="evidence-code-input" value={code} onChange={(e) => setCode(e.target.value)} autoFocus maxLength={20} inputMode="numeric" autoComplete="off"
              className="form-input w-[220px] font-mono text-3xl tracking-[0.3em]"
              data-testid="evidence-code-input"
            />
            <button className="btn-spore" disabled={busy || !code.trim()} data-testid="evidence-code-submit"><KeyRound size={18} /> Submit</button>
          </div>
        </form>
        {result && (
          <div className={`px-5 py-3 text-xl font-bold ${TONE[result.result]}`} data-testid="evidence-locker-message" data-result={result.result}>
            {result.message}
          </div>
        )}
        <div className="mt-4">
          <h3 className="text-xl font-black mb-3" data-testid="locker-count">Recovered evidence {recovered.length}/4</h3>
          <div className="border-t border-[#b1b4b6]" data-testid="locker-items">
            {recovered.map((sid) => (
              <button key={sid} onClick={() => open(sid)} className="w-full flex items-center gap-3 border-b border-[#b1b4b6] px-2 py-3 text-left hover:bg-[#f3f2f1]" data-testid={`locker-item-${sid}`}>
                <FileText className="text-[#0033A1]" size={20} />
                <span className="text-lg link">{state.suspects[sid].clue.title}</span>
                <span className="ml-auto text-[#505a5f]">{state.suspects[sid].name}</span>
              </button>
            ))}
            {Array.from({ length: 4 - recovered.length }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 border-b border-[#b1b4b6] px-2 py-3 text-[#505a5f]">
                <Lock size={18} /> <span>Not yet submitted</span>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
